import { throwError, Observable, BehaviorSubject, EMPTY } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { AuthenticationTokenService } from '../authentication/authentication-token.service';
import { LoginObserverService } from '../authentication/login-observer.service';
import { catchError, first, map } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class JsonHttpService {
    private errorHandlerSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    errorHandler: Observable<any> = this.errorHandlerSubject.asObservable();

    constructor(
        @Inject('BASE_URL') private baseUrl: string,
        private httpClient: HttpClient,
        private tokenService: AuthenticationTokenService,
        private loginObserverService: LoginObserverService) {

        if (!this.baseUrl.endsWith('/')) {
            this.baseUrl += '/';
        }
    }

    generateBackendUrl(url: string): string {
        if (url.startsWith('/')) {
            url = url.substring(1);
        }
        return this.baseUrl + url;
    }

    generateHeaders(byPassCredentials = false): HttpHeaders {
        let headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        if (!byPassCredentials) {
            const accessToken = this.tokenService.getAccessToken();
            if (accessToken) {
                headers = headers.set('Authorization', `Bearer ${accessToken}`);
            }
        }

        return headers;
    }

    getTyped<T>(url: string, options?: any, useGenericErrorHandling = true, byPassCredentials = false): Observable<T> {
        return this.get(url, options, useGenericErrorHandling, byPassCredentials).pipe(
            map(response => response as T)
        );
    }

    get(url: string, options?: any, useGenericErrorHandling = true, byPassCredentials = false): Observable<any> {
        if (this.tokenService.hasAccessToken() || byPassCredentials) {
            return this.getInternal(url, options, useGenericErrorHandling, byPassCredentials);
        }
        return new Observable<any>(observer => {
            this.tokenService.waitForAccessTokenObservable.pipe(
                first(e => !!e)
            ).subscribe(() => {
                this.getInternal(url, options, useGenericErrorHandling, byPassCredentials).subscribe(
                    value => observer.next(value),
                    error => observer.error(error),
                    () => observer.complete()
                );
            });
        });
    }

    getWithErrorEval(url: string, options?: any, useGenericErrorHandling?: (err: any, caught: Observable<any>) => boolean): Observable<any> {
        return this.get(url, options, false).pipe(
            catchError((err: any, caught: Observable<any>) => {
                const useGenericErrorHandlingResult = useGenericErrorHandling(err, caught);
                return this.handleError(err, useGenericErrorHandlingResult);
            })
        );
    }

    getRaw(url: string, options?: any, setHeaders?: boolean, useGenericErrorHandling = true): Observable<any> {
        options = options || {};
        if (setHeaders) {
            options.headers = this.generateHeaders();
        }

        return this.httpClient.get(this.generateBackendUrl(url), options).pipe(
            catchError((e: HttpErrorResponse) => this.handleError(e, useGenericErrorHandling))
        );
    }

    postWithErrorEval(url: string, data: any, options?: any, useGenericErrorHandling?: (err: any, caught: Observable<any>) => boolean): Observable<any> {
        return this.post(url, data, options, false).pipe(
            catchError((err: any, caught: Observable<any>) => {
                const useGenericErrorHandlingResult = useGenericErrorHandling(err, caught);
                return this.handleError(err, useGenericErrorHandlingResult);
            })
        );
    }

    postTyped<T>(url: string, data: unknown, options?: unknown, useGenericErrorHandling: boolean = true): Observable<T> {
        return this.post(url, data, options, useGenericErrorHandling).pipe(
            map(response => response as T)
        );
    }

    post(url: string, data: any, options?: any, useGenericErrorHandling: boolean = true): Observable<any> {
        if (this.tokenService.hasAccessToken()) {
            return this.postInternal(url, data, options, useGenericErrorHandling);
        }

        return new Observable<any>(observer => {
            this.tokenService.waitForAccessTokenObservable.pipe(
                first(e => !!e)
            ).subscribe(() => {
                this.postInternal(url, data, options, useGenericErrorHandling).subscribe(
                    value => observer.next(value),
                    error => observer.error(error),
                    () => observer.complete()
                );
            });
        });
    }

    postWithoutAccessToken(url: string, data: any, options?: any, useGenericErrorHandling = true): Observable<any> {
        return this.postInternal(url, data, options, useGenericErrorHandling);
    }

    getWithoutAccessToken(url: string, options?: any, useGenericErrorHandling = true): Observable<any> {
        return this.getInternal(url, options, useGenericErrorHandling, true);
    }

    postRaw(url: string, data: any, options?: any, useGenericErrorHandling = true): Observable<any> {
        options = options || {};
        options.headers = options.headers || this.generateHeaders();

        return this.httpClient.post(this.generateBackendUrl(url), data, options).pipe(
            catchError((e: HttpErrorResponse) => this.handleError(e, useGenericErrorHandling))
        );
    }

    put(url: string, data: any, options?: any, useGenericErrorHandling = true): Observable<any> {
        options = options || {};
        options.headers = options.headers || this.generateHeaders();

        return this.httpClient.put(this.generateBackendUrl(url), data, options).pipe(
            catchError((e: HttpErrorResponse) => this.handleError(e, useGenericErrorHandling))
        );
    }

    delete(url: string, options?: any, useGenericErrorHandling = true): Observable<any> {
        options = options || {};
        options.headers = options.headers || this.generateHeaders();

        return this.httpClient.delete(this.generateBackendUrl(url), options).pipe(
            catchError((e: HttpErrorResponse) => this.handleError(e, useGenericErrorHandling))
        );
    }

    private getInternal(url: string, options: any, useGenericErrorHandling: boolean, byPassCredentials: boolean): Observable<any> {
        options = options || {};
        options.headers = options.headers || this.generateHeaders(byPassCredentials);

        return this.httpClient.get(this.generateBackendUrl(url), options).pipe(
            catchError((e: HttpErrorResponse) => this.handleError(e, useGenericErrorHandling))
        );
    }

    private postInternal(url: string, data: any, options: any, useGenericErrorHandling: boolean): Observable<any> {
        options = options || {};
        options.headers = options.headers || this.generateHeaders();

        return this.httpClient.post(this.generateBackendUrl(url), data, options).pipe(
            catchError((e: HttpErrorResponse) => this.handleError(e, useGenericErrorHandling))
        );
    }

    private handleError(error: HttpErrorResponse, useGenericErrorHandling: boolean): Observable<never> {
        if (useGenericErrorHandling) {
            if (error?.status === 490) {
                console.log('AZURE AD UNAUTHORIZED!', error);
                this.loginObserverService.requireAzureAdLogin();
                return EMPTY; // Return empty observable to prevent the calling code to execute (which might cause 'undefined' errors)
            }

            if (error?.status === 491) {
                console.log('AZURE AD INSUFFICIENT PERMISSIONS!', error);
                this.loginObserverService.notifyInsufficientLoginPermisssions();
                return EMPTY; // Return empty observable to prevent the calling code to execute (which might cause 'undefined' errors)
            }

            if (error?.status === 401) {
                console.log('UNAUTHENTICATED!', error);
                return EMPTY; // Return empty observable to prevent the calling code to execute (which might cause 'undefined' errors)
            }

            if (error?.status === 403) {
                console.log('ACCESS FORBIDDEN!', error);
                this.loginObserverService.notifyAccessForbidden(error);
                return EMPTY; // Return empty observable to prevent the calling code to execute (which might cause 'undefined' errors)
            }

            this.errorHandlerSubject.next(error);
        }
        return throwError(error);
    }
}
